import { useEffect, useMemo, useState } from 'react';
import {
  fetchDemCustomer,
  fetchDemCustomersBlockedByCodes,
  fetchPowerBiCustomerDetail,
} from './demCustomers';
import { fetchCustItemBlocks } from './demCustItemBlockList';
import { fetchItemDetails, type DemItemDetail } from './demSkuMap';
import type { DemUnifiedOrder } from '../types/dem';

/** Normalize NAV Blocked (Ship / Invoice / All / boolean) for display. */
export function formatCustomerBlockedLabel(raw: string | boolean | null | undefined): string {
  if (raw === true) return 'All';
  if (raw === false || raw == null) return '';
  const s = String(raw).trim();
  if (!s) return '';
  const lower = s.toLowerCase();
  if (['false', '0', 'no', 'n', ''].includes(lower)) return '';
  if (['true', '1', 'yes', 'y'].includes(lower)) return 'All';
  if (lower === 'ship') return 'Ship';
  if (lower === 'invoice') return 'Invoice';
  if (lower === 'all') return 'All';
  return s;
}

export function customerBlockedWarning(raw: string | boolean | null | undefined): string {
  return formatCustomerBlockedLabel(raw) ? '⚠ Customer blocked' : '';
}

export function itemBlockedWarning(opts: {
  isBlocked: boolean;
  custItemBlockReason?: string;
}): string {
  const reason = opts.custItemBlockReason?.trim() ?? '';
  if (reason && opts.isBlocked) return `⚠ Blocked item · ${reason}`;
  if (reason) return `⚠ ${reason}`;
  if (opts.isBlocked) return '⚠ Blocked item';
  return '';
}

export type OrderItemBlockInfo = {
  isBlocked: boolean;
  custItemBlockReason: string;
  warning: string;
};

export type OrderBlockWarnings = {
  customerWarning: string;
  itemWarnings: Map<string, OrderItemBlockInfo>;
  loading: boolean;
};

/**
 * Load customer NAV blocked (Ship/Invoice/All) + item master blocked +
 * Cust_Item_Block_List reasons for an order detail view.
 */
export function useOrderBlockWarnings(
  customerCode: string | null | undefined,
  itemCodes: string[],
  enabled = true,
): OrderBlockWarnings {
  const [customerWarning, setCustomerWarning] = useState('');
  const [itemWarnings, setItemWarnings] = useState<Map<string, OrderItemBlockInfo>>(new Map());
  const [loading, setLoading] = useState(false);

  const codesKey = [...new Set(itemCodes.map((c) => c.trim()).filter(Boolean))]
    .sort()
    .join(',');
  const customer = customerCode?.trim() ?? '';

  useEffect(() => {
    if (!enabled || (!customer && !codesKey)) {
      setCustomerWarning('');
      setItemWarnings(new Map());
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const codes = codesKey ? codesKey.split(',') : [];

    void (async () => {
      try {
        const [detail, demCustomer, itemDetails, custBlocks] = await Promise.all([
          customer ? fetchPowerBiCustomerDetail(customer).catch(() => null) : Promise.resolve(null),
          customer ? fetchDemCustomer(customer).catch(() => null) : Promise.resolve(null),
          codes.length > 0
            ? fetchItemDetails(codes).catch(() => ({} as Record<string, DemItemDetail>))
            : Promise.resolve({} as Record<string, DemItemDetail>),
          customer && codes.length > 0
            ? fetchCustItemBlocks(customer, codes).catch(() => new Map<string, string>())
            : Promise.resolve(new Map<string, string>()),
        ]);

        if (cancelled) return;

        const fromPowerBi = customerBlockedWarning(detail?.blocked);
        const fromDem =
          !fromPowerBi && demCustomer?.is_blocked ? customerBlockedWarning('All') : '';
        setCustomerWarning(fromPowerBi || fromDem);

        const next = new Map<string, OrderItemBlockInfo>();
        for (const code of codes) {
          const upper = code.toUpperCase();
          const isBlocked = Boolean(itemDetails[code]?.is_blocked ?? itemDetails[upper]?.is_blocked);
          const custItemBlockReason = custBlocks.get(upper) ?? custBlocks.get(code) ?? '';
          next.set(upper, {
            isBlocked,
            custItemBlockReason,
            warning: itemBlockedWarning({ isBlocked, custItemBlockReason }),
          });
        }
        setItemWarnings(next);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [customer, codesKey, enabled]);

  return { customerWarning, itemWarnings, loading };
}

function orderHasItemBlock(
  order: DemUnifiedOrder,
  blockedItems: Record<string, DemItemDetail>,
  custBlocks: Map<string, string> | undefined,
): boolean {
  for (const item of order.items ?? []) {
    const raw = item.item_code?.trim() ?? '';
    if (!raw) continue;
    const upper = raw.toUpperCase();
    const isBlocked = Boolean(blockedItems[raw]?.is_blocked ?? blockedItems[upper]?.is_blocked);
    const reason = custBlocks?.get(upper) ?? custBlocks?.get(raw) ?? '';
    if (isBlocked || reason) return true;
  }
  return false;
}

/** Which orders have a blocked customer and/or blocked item (master or cust×item list). */
export async function fetchOrderListBlockFlags(orders: DemUnifiedOrder[]): Promise<Set<string>> {
  const flagged = new Set<string>();
  if (orders.length === 0) return flagged;

  const customerCodes: string[] = [];
  const itemsByCustomer = new Map<string, Set<string>>();
  const allItemCodes = new Set<string>();

  for (const order of orders) {
    const cust = order.customer_code?.trim() ?? '';
    if (cust) customerCodes.push(cust);
    for (const item of order.items ?? []) {
      const code = item.item_code?.trim() ?? '';
      if (!code) continue;
      allItemCodes.add(code);
      if (cust) {
        const bucket = itemsByCustomer.get(cust) ?? new Set<string>();
        bucket.add(code);
        itemsByCustomer.set(cust, bucket);
      }
    }
  }

  const [blockedCustomers, itemDetails] = await Promise.all([
    fetchDemCustomersBlockedByCodes(customerCodes).catch(() => new Set<string>()),
    allItemCodes.size > 0
      ? fetchItemDetails([...allItemCodes]).catch(() => ({} as Record<string, DemItemDetail>))
      : Promise.resolve({} as Record<string, DemItemDetail>),
  ]);

  const custBlockMaps = new Map<string, Map<string, string>>();
  await Promise.all(
    [...itemsByCustomer.entries()].map(async ([cust, items]) => {
      const map = await fetchCustItemBlocks(cust, [...items]).catch(() => new Map<string, string>());
      custBlockMaps.set(cust, map);
    }),
  );

  for (const order of orders) {
    const cust = order.customer_code?.trim() ?? '';
    if (cust && blockedCustomers.has(cust)) {
      flagged.add(order.order_id);
      continue;
    }
    if (orderHasItemBlock(order, itemDetails, cust ? custBlockMaps.get(cust) : undefined)) {
      flagged.add(order.order_id);
    }
  }

  return flagged;
}

export function useOrderListBlockFlags(orders: DemUnifiedOrder[]): Set<string> {
  const [flagged, setFlagged] = useState<Set<string>>(() => new Set());
  const scanKey = useMemo(
    () =>
      orders
        .map(
          (o) =>
            `${o.order_id}\t${o.customer_code ?? ''}\t${(o.items ?? [])
              .map((i) => i.item_code)
              .join(',')}`,
        )
        .join('\n'),
    [orders],
  );

  useEffect(() => {
    let cancelled = false;
    if (orders.length === 0) {
      setFlagged(new Set());
      return;
    }
    void fetchOrderListBlockFlags(orders).then((next) => {
      if (!cancelled) setFlagged(next);
    });
    return () => {
      cancelled = true;
    };
  }, [scanKey]);

  return flagged;
}
